import { useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { TANZANIAN_REGIONS } from "@/lib/localization";

export interface UniversityOption {
  id: number;
  name: string;
  abbreviation: string;
  location: string;
  type: string;
}

export interface ProgrammeOption {
  id: number;
  university_id: number;
  name: string;
  award_level: string;
  duration: string;
  study_mode: string;
}

export interface PaymentPartner {
  id: string | number;
  partner_name: string;
  prefixes?: string;
  logo_url?: string;
  currency?: string;
}

export function useUniversities(search?: string, selectedId?: number | null) {
  return useQuery({
    queryKey: ["tcu-universities", search, selectedId],
    queryFn: async () => {
      try {
        const res = await apiClient.get("/tcu/universities", {
          params: { search, include_id: selectedId },
        });
        return res.data.data as UniversityOption[];
      } catch {
        // No made-up fallback list: its ids did not exist in the database, so a
        // student who picked from it was saved against the wrong institution.
        return [] as UniversityOption[];
      }
    },
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
}

export function useProgrammes(universityId?: number, search?: string) {
  return useQuery({
    queryKey: ["tcu-programmes", universityId, search],
    queryFn: async () => {
      if (!universityId) return [];
      try {
        const res = await apiClient.get(`/tcu/universities/${universityId}/programmes`, {
          params: { search },
        });
        return res.data.data as ProgrammeOption[];
      } catch {
        // No demo fallback: it showed UDSM courses under whichever institution
        // happened to have id 1 or 3.
        return [] as ProgrammeOption[];
      }
    },
    enabled: !!universityId,
    placeholderData: undefined,
    staleTime: 1000 * 60 * 10,
  });
}

export function useIndustries() {
  return useQuery({
    queryKey: ["industries"],
    queryFn: async () => {
      try {
        const res = await apiClient.get("/reference/industries", {
          skipGlobalErrorHandler: true,
        } as any);
        return res.data.data || [];
      } catch {
        return [];
      }
    },
  });
}

export function useSkills() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["skills"],
    queryFn: async () => {
      if (user?.isDemo) {
        return defaultSkills;
      }
      try {
        const res = await apiClient.get("/reference/skills", {
          skipGlobalErrorHandler: true,
        } as any);
        return res.data.data || [];
      } catch {
        return defaultSkills;
      }
    },
  });
}

// Default fallback skills for demo/offline mode
const defaultSkills = [
  "Web Development", "Mobile Development", "Cloud Computing", "DevOps",
  "Data Analysis", "Cybersecurity", "Database Management", "API Development",
  "Financial Analysis", "Accounting", "Risk Management", "Auditing",
  "Network Administration", "Customer Service", "Quality Control",
  "Supply Chain", "Inventory Management", "Marketing", "Sales",
  "Project Management", "CAD Drawing", "Health and Safety",
];

export function useRegions() {
  return useQuery({
    queryKey: ["regions"],
    queryFn: async () => {
      try {
        const res = await apiClient.get("/reference/regions", {
          skipGlobalErrorHandler: true,
        } as any);
        return res.data.data as string[];
      } catch {
        return TANZANIAN_REGIONS;
      }
    },
  });
}

export function usePaymentPartners() {
  return useQuery({
    queryKey: ["payment-partners"],
    queryFn: async () => {
      try {
        const res = await apiClient.get("/payment/partners");
        return res.data.data as PaymentPartner[];
      } catch {
        return [
          { id: "mpesa", partner_name: "M-Pesa", prefixes: "74,75,76", currency: "TZS" },
          { id: "tigopesa", partner_name: "Tigo Pesa", prefixes: "65,67,71", currency: "TZS" },
          {
            id: "airtelmoney",
            partner_name: "Airtel Money",
            prefixes: "68,69,78",
            currency: "TZS",
          },
          { id: "halopesa", partner_name: "HaloPesa", prefixes: "62", currency: "TZS" },
        ] as PaymentPartner[];
      }
    },
  });
}
